import type { Router } from 'vue-router'
import { useClipboard } from '@vueuse/core'
import { hasOwnBridge, isApl, isIOS } from '@/env'
import { useSystemInfo, useUserInfo } from '@/store'
import type { iGame, iGameCategory } from '@/types/index'
import bcrypt from 'bcryptjs'
import FingerprintJS from '@fingerprintjs/fingerprintjs'
import { FINGER_PRINT_KEY, SALT_KEY } from '@/consts'
import dayjs from 'dayjs'
import { handleSaqueClick } from '@/utils/userStatusUtils'
import { reportErrorMessage } from '@/utils/reportError'

export const lowPriorityAsyncCall = (fn: () => void) => {
  let callFn: (fn: () => void) => void
  if ('requestIdleCallback' in window) {
    callFn = requestIdleCallback
  } else {
    callFn = setTimeout
  }
  callFn(fn)
}

export const wait = (seconds: number) => {
  return new Promise((resolve) => {
    setTimeout(resolve, seconds * 1000)
  })
}

export const downloadFile = (url, fileName) => {
  const a = document.createElement('a')
  a.href = url
  a.target = '_self'
  a.download = fileName
  a.click()
}

// const isFromDrainageWebsite = () => {
//   const drainageArr = ['facebook', 'instagram']
//   return drainageArr.some((item) => document.referrer.includes(item))
// }

export const launchChrome = (s = location.href) => {
  // if (isChrome) return

  const url = s.replace(/^http(s)?:\/\//, '')

  if (isIOS) {
    location.href = 'googlechrome://' + url
  } else {
    location.href = `intent://${url}#Intent;package=com.android.chrome;scheme=http;end;`
    // window.open(`intent://${url}#Intent;package=com.android.chrome;scheme=http;end;`, '_blank')
  }
}

export const gotoActivity = (item, router: Router) => {
  router.push({
    path: item.link,
    query: {
      activityId: item.activityId,
    },
    state: {
      // currentActivity: toRaw(item)
    },
  })
}

export const handleJumpLink = (item, router) => {
  if (item.hyperlinkType == 'NONE') return

  if (item.hyperlink) {
    if (item.hyperlinkType === 'CUSTOM_LINK') {
      if (item.jumpMethod === 'EXTERNAL_JUMP') {
        window.open(item.hyperlink, '_blank')
      } else {
        window.location.href = item.hyperlink
      }
    } else {
      if (item.hyperlink === '/saque') {
        // 提款页先展示弹窗
        // updateIsOpenStatus();
        //
        handleSaqueClick(router)
      } else {
        gotoActivity(
          {
            activityId: item.activityId,
            link: item.hyperlink,
          },
          router,
        )
      }
    }
  }
}

export const gotoGameCategory = (item: iGameCategory, router: Router) => {
  router.push({
    path: '/gamecategory',
    query: {
      cid: item.id,
    },
    state: {
      // @ts-ignore
      currentCategory: toRaw(item),
    },
  })
}

export const gotoGame = (item: iGame, router: Router, list: iGame[]) => {
  if (useUserInfo().isLoggedin) {
    router.push(`/game?gameCode=${item.code}`)
    reportErrorMessage(item, list, {
      page: 'gameLogin',
      reqUrl: `/game/login`,
    })
  } else {
    router.push('/login')
  }
}

export const formatter = (mindigits = 2, maxDigits = 2) => {
  const langStr = useSystemInfo().langStr

  return new Intl.NumberFormat(langStr, {
    style: 'decimal',
    minimumFractionDigits: mindigits,
    maximumFractionDigits: maxDigits,
  })
}

export const copyText = (text: string, skipToast = false) => {
  const { copy } = useClipboard()
  return copy(text)
    .then(() => {
      !skipToast &&
        showToast({
          icon: 'success',
          iconSize: '30px',
          message: 'Cópia Bem-sucedida',
          zIndex: 9999,
        })
    })
    .catch(() => {
      !skipToast &&
        showToast({
          icon: 'fail',
          message: 'Cópia falhou',
          zIndex: 9999,
        })
    })
    .finally(() => {
      if (document.activeElement) (document.activeElement as HTMLElement).blur()
    })
}

export const readText = async () => {
  try {
    return await navigator.clipboard.readText()
  } catch (err) {
    console.error('读取剪贴板内容失败:', err)
    throw err
  }
}

//密码加密
export const encryptPassword = (password: string) => {
  return bcrypt.hashSync(password, SALT_KEY)
}

export const cryptoID = (eventName: string) => {
  return eventName + '-' + localStorage.getItem('pwaId')
}

export async function generateFingerprint() {
  const fp = await FingerprintJS.load()
  const result = await fp.get()
  localStorage.setItem(FINGER_PRINT_KEY, result.visitorId)
  return result.visitorId
}

// 格式化金额
export const formatNumber = (value: number, mindigits = 2, maxDigits = 2) => {
  const number = parseFloat(value as any)

  if (isNaN(number)) {
    return value // 如果不是有效数字，直接返回原值
  }

  if (number >= 1000000) {
    const formattedValue = number / 1000000
    return `${formatter(mindigits, maxDigits).format(formattedValue)}M`
  }

  if (number >= 1000) {
    const formattedValue = number / 1000
    return `${formatter(mindigits, maxDigits).format(formattedValue)}K`
  }

  return formatter(mindigits, maxDigits).format(value)
}

// 1. 前端本地引用 local://live.png 把local://替换成本地图片路径后打开
// 2. OSS存储引用 oss://a/b/live.png 把oss://替换成OSS图片地址后打开
// 3. HTTP直接 http://xxxx.com/a/b/live.png 直接打开图片
export const getImageUrl = (url: string) => {
  const { imageBaseUrl } = useSystemInfo()
  const localPath = `/static/images/defaults/`
  return url.replace('local://', localPath).replace('oss://', imageBaseUrl)
}

// 获取日期范围
export const getDateRange = (range: string) => {
  const today = dayjs().tz('America/Sao_Paulo')
  console.log('today', today.valueOf())
  const ranges = {
    Hoje: [today.startOf('day').valueOf(), today.endOf('day').valueOf()],
    Ontem: [
      today.subtract(1, 'day').startOf('day').valueOf(),
      today.subtract(1, 'day').endOf('day').valueOf(),
    ],
    'Esta semana': [
      today.startOf('week').valueOf(),
      today.endOf('week').valueOf(),
    ],
    'Este mês': [
      today.startOf('month').valueOf(),
      today.endOf('month').valueOf(),
    ],
    'Mês passado': [
      today.subtract(1, 'month').startOf('month').valueOf(),
      today.subtract(1, 'month').endOf('month').valueOf(),
    ],
    'Últimos três meses': [
      today.subtract(90, 'day').startOf('day').valueOf(),
      today.endOf('day').valueOf(),
    ],
  }

  return ranges[range] || ranges['Hoje']
}

// 格式化成巴西时间格式
export const formatDateInPortuguese = (dateString) => {
  const months = [
    'jan.',
    'fev.',
    'mar.',
    'abr.',
    'mai.',
    'jun.',
    'jul.',
    'ago.',
    'set.',
    'out.',
    'nov.',
    'dez.',
  ]
  const date = dayjs(dateString)
  const day = date.date()
  const month = months[date.month()]
  const year = date.year()
  const hours = date.hour().toString().padStart(2, '0')
  const minutes = date.minute().toString().padStart(2, '0')
  const seconds = date.second().toString().padStart(2, '0')

  return `${day} de ${month} de ${year} ${hours}:${minutes}:${seconds}`
}

export const useFormattedDate = (dateString) => {
  const formatDateInPortuguese = (date) => {
    const months = [
      'jan.',
      'fev.',
      'mar.',
      'abr.',
      'mai.',
      'jun.',
      'jul.',
      'ago.',
      'set.',
      'out.',
      'nov.',
      'dez.',
    ]
    const day = date.date()
    const month = months[date.month()]
    const year = date.year()

    return `${day} de ${month} de ${year}`
  }

  const date = dayjs(dateString)
  const formattedDate = formatDateInPortuguese(date)

  return formattedDate
}

export const fetchWithLoading = async (
  fn: () => Promise<any>,
  {
    loadingText = 'loading...',
    errorText = 'error',
    successText = '',
    onSuccess = () => {},
  }: {
    loadingText?: string
    errorText?: string
    successText?: string
    onSuccess?: () => void
  } = {},
) => {
  showLoadingToast(loadingText)
  try {
    const res = await fn()
    if (res.code == 1) {
      if (successText) {
        showSuccessToast({
          iconSize: 30,
          message: successText,
          onClose: onSuccess,
        })
      } else {
        closeToast()
      }
    } else {
      showToast(res.msg)
    }
    return res
  } catch (error) {
    showToast(errorText)
  }
}

// 次数
export const formatCount = (v, unit = 'K') => {
  if (v < 1000) {
    return v
  } else {
    return Math.floor(v / 1000) + unit
  }
}

export const callJSBridge = (eventName, params = {}) => {
  // console.log('callJSBridge', eventName, params)
  window.jsBridge?.postMessage(eventName, JSON.stringify(params))
}

export const createObjfromStr = (str: string) => {
  const obj = {}
  const arr = str.split('&')
  arr.forEach((item) => {
    const [key, value] = item.split('=')
    obj[key] = value
  })
  return obj
}

export const getAuthStr = (userInfo) => {
  let s = ''
  try {
    if (userInfo && userInfo.token) {
      const lUserInfo = getUserNameAndPassword()
      let url = `token=${userInfo.token}`
      if (lUserInfo.username && lUserInfo.password) {
        url = `${url}&un_flag=${lUserInfo.username}&ps_flag=${lUserInfo.password}`
      }
      s = 'info=' + btoa(encodeURIComponent(url))
      if (s === 'info=null') {
        s = ''
      }
    }
  } catch (e) {
    console.error('getAuthStr', e)
  }
  return s
}

export const back = (router: Router) => {
  const t = location.href
  router.back()

  setTimeout(() => {
    if (location.href == t) {
      router.push('/home')
    }
  }, 300)
}

export const formatNumberAsMoney = (num: string) => {
  const parts = num.split(',')
  if (parts.length > 1 && Number(parts[1]) > 0) {
    return num.endsWith('0') ? num.slice(0, -1) : num
  } else {
    return parts[0]
  }
}

// 保存登录使用的用户名密码
export const saveUserNameAndPassword = (
  username: string | null | undefined,
  password: string | null | undefined,
  isAutoLogin = false,
) => {
  if (!username || !password) {
    return
  }
  //自动登录时，判断当前是否已经有保存用户名密码，没有的话再去保存，如果有的话就不要覆盖手动登录的用户名密码了
  if (isAutoLogin) {
    console.log('自动登录时，判断当前是否已经有保存用户名密码')
    const lUserInfo = getUserNameAndPassword()
    if (lUserInfo.username && lUserInfo.password) {
      console.log('自动登录时，已经有保存的用户名密码了，就不再保存了')
      return
    }
  }
  // 在自有安卓包,通过JSBridge保存账号密码，否则直接保存到localStorage
  if (hasOwnBridge) {
    console.log('在自有安卓包,准备调用这个,保存账号密码的接口')
    console.log('isApl', isApl)
    const params2 = new URLSearchParams(location.search)
    console.log('params2', params2)
    window.jsBridge.postMessage(
      'setUserInfo',
      JSON.stringify({ username, password }),
    )
  } else {
    localStorage.setItem('un_flag', username)
    localStorage.setItem('ps_flag', password)
    console.log('保存好了，测试取一下')
    console.log('getUserNameAndPassword-->lUserInfo', getUserNameAndPassword())
  }
}

// 获取登录使用的用户名密码
export const getUserNameAndPassword = () => {
  let lUserInfo: { username: string; password: string } = {
    username: '',
    password: '',
  }
  if (hasOwnBridge) {
    //从JSBridge读取
    const res = window.jsBridge.postMessage('getUserInfo', '')
    console.log('在自有安卓包,取得的用户名密码:', res)
    //兼容老版本APK，有可能这里获取出来是ok
    if (
      res === 'ok' ||
      res === '' ||
      res === 'undefined' ||
      res === null ||
      res === undefined
    ) {
      return { username: '', password: '' }
    }
    const r = JSON.parse(res)
    lUserInfo.username = r.username
    lUserInfo.password = r.password
  } else {
    if (localStorage.getItem('un_flag') && localStorage.getItem('ps_flag')) {
      lUserInfo.username = localStorage.getItem('un_flag') || ''
      lUserInfo.password = localStorage.getItem('ps_flag') || ''
    }
  }
  console.log('getUserNameAndPassword-->lUserInfo', lUserInfo)
  return lUserInfo
}

//这个功能暂时不用
export const clearUserNameAndPassword = () => {
  // localStorage.removeItem('un_flag')
  // localStorage.removeItem('ps_flag')
  // if (isInAndroidApp) {
  //   window.jsBridge.postMessage("clearUserInfo", "");
  // }
}

// 获取 CSS 变量值的方法
export const getCssVar = (name: string) => {
  return getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim()
}

// 将 rem 转换为 px
export const remToPx = (rem: string) => {
  return (
    parseFloat(rem) *
    parseFloat(getComputedStyle(document.documentElement).fontSize)
  )
}
